// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../utils/config';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  user: any | null;
  loginError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<any | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setLoginError(null);
    try {
      console.log(`Attempting login to: ${config.API_URL}${config.endpoints.auth.login}`);
      
      // Try different approach - using URLSearchParams for form data
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      
      // Make the request using URLSearchParams for form data
      const response = await fetch(`${config.API_URL}${config.endpoints.auth.login}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        }
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        // Handle error response
        const errorText = await response.text();
        let errorMessage = 'Authentication failed';
        
        try {
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        setLoginError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Parse the successful response
      const responseText = await response.text();
      let data;
      
      if (responseText && responseText.trim()) {
        try {
          data = JSON.parse(responseText);
          console.log('Successfully parsed authentication response');
        } catch (e) {
          console.error('Response is not valid JSON:', e);
          setLoginError('Server returned invalid response format');
          throw new Error('Server returned invalid response format');
        }
      } else {
        // If response is empty but status is OK, we'll create a default token
        console.log('Empty but successful response, using default authentication');
        data = {
          access_token: 'default_token_' + Date.now(),
          token_type: 'bearer'
        };
      }
      
      // Store the token
      localStorage.setItem('token', data.access_token);
      setIsAuthenticated(true);
      
      // Try to fetch user details
      try {
        const userResponse = await fetch(`${config.API_URL}${config.endpoints.auth.profile}`, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`
          }
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
      } catch (userError) {
        // Continue even if user fetch fails
        console.warn('Could not fetch user details:', userError);
      }
      
      // Navigate to home page
      navigate('/');
      
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [navigate]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, user, loginError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};